import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { CardContainer } from "@msys/ui";
import {
  Alert,
  Button,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { FieldArray, Form, Formik } from "formik";
import React from "react";
import { FormikClearErrorsOnChange } from "../../../../commons/FormikClearErrorsOnChange.js";
import { TextField } from "../../../../commons/form-fields/TextField.js";
import { useUnsavedChangesGuard } from "../../../../commons/hooks/useUnsavedChangesGuard.js";
import type { IntegrationSettingsProps } from "../../integrationRegistry.js";
import { getIntegrationMeta } from "../../integrationRegistry.js";
import {
  CardSaveRow,
  FormDirtyTracker,
  useCardDirtyRegistry,
} from "./integrationCardForm.js";
import { SelectField } from "../../../../commons/form-fields/SelectField.js";
import {
  API_ENDPOINT_GROUPS,
  apiEndpointsInitialValues,
  type BusinessPartnersValues,
  businessPartnersInitialValues,
  type CrmGdprValues,
  crmGdprInitialValues,
  CUSTOM_FIELD_ENTITIES,
  type CustomFieldsValues,
  customFieldsInitialValues,
  duplicateBpFunctionCodeIndexes,
  duplicateCustomFieldIndexes,
  duplicateRoleIndexes,
  duplicateStepIndexes,
  type ExpertValues,
  expertInitialValues,
  type GeneralValues,
  generalInitialValues,
  isValidPricingAccessLevel,
  type ItemsValues,
  itemsInitialValues,
  MIN_PRICING_ACCESS_LEVEL,
  type OrganisationValues,
  organisationInitialValues,
  parseExpertSettings,
  type PricingVisibilityValues,
  pricingStepAccessLevelsError,
  pricingVisibilityInitialValues,
  type ProductsValues,
  productsInitialValues,
  type QuotesValues,
  quotesInitialValues,
  type SettingsObject,
  toApiEndpointsSlice,
  toBusinessPartnersSlice,
  toCrmGdprSlice,
  toCustomFieldsSlice,
  toGeneralSlice,
  toItemsSlice,
  toOrganisationSlice,
  toPricingVisibilitySlice,
  toProductsSlice,
  toQuotesSlice,
  type ApiEndpointsValues,
} from "./sapS4HanaSettingsSlices.js";
import { SettingsPageLayout } from "./SettingsPageLayout.js";
import { useIntegrationSettingsBase } from "./useIntegrationSettingsBase.js";

// Settings paths whose form-field name differs from the schema path (the
// flat form renames nested keys); everything else maps by its first path
// segment
const SERVER_PATH_TO_FIELD: Record<string, string> = {
  "crm.userIdcustomFieldKey": "crmUserIdCustomFieldKey",
  "crm.defaultJobTitle": "crmDefaultJobTitle",
  "crm.defaultCashPlanningGroup": "crmDefaultCashPlanningGroup",
  "crm.endClientPaymentTerms": "crmEndClientPaymentTerms",
  "crm.endClientZugferdMailEnabled": "crmEndClientZugferdMailEnabled",
  "gdprEmailConfig.fromEmailAddess": "gdprFromEmailAddress",
  "gdprEmailConfig.emailTemplateId": "gdprEmailTemplateId",
  "salesQuotationItemPositionSettings.positionStepSize": "positionStepSize",
  "salesQuotationItemPositionSettings.roundDistributedPositionsTo":
    "roundDistributedPositionsTo",
  "salesQuotationItemPositionSettings.firstPosition": "firstPosition",
  "salesQuotationItemPositionSettings.minPosition": "minPosition",
  "salesQuotationItemPositionSettings.maxPosition": "maxPosition",
};

// A settings-validation failure surfaces as a serialized issue array. Only
// the paths are read (to highlight fields) — the raw text, which echoes the
// received values, is never rendered.
function serverValidationPaths(message: string): string[] | null {
  try {
    const parsed: unknown = JSON.parse(message);
    if (!Array.isArray(parsed)) return null;
    const paths: string[] = [];
    for (const issue of parsed) {
      if (issue && typeof issue === "object") {
        const path = (issue as Record<string, unknown>).path;
        if (Array.isArray(path) && path.length > 0) {
          paths.push(path.map(String).join("."));
        }
      }
    }
    return paths.length > 0 ? paths : null;
  } catch {
    return null;
  }
}

/**
 * Map a server issue path onto a form field name. For the record-shaped
 * settings the second path segment is the record key, which identifies the
 * repeatable row to highlight rather than the whole array.
 */
function serverPathToFormField(path: string, cardValues: object): string {
  // Expert mode: every issue points at the single JSON editor
  if (Object.prototype.hasOwnProperty.call(cardValues, "settingsJson")) {
    return "settingsJson";
  }
  const mapped = SERVER_PATH_TO_FIELD[path];
  if (mapped) return mapped;
  const [head, second] = path.split(".");
  // Compare against the trimmed row values: the slice mappers trim record
  // keys on save, so the server path carries the trimmed key while the form
  // may still hold surrounding whitespace.
  if (head === "salesQuotationBusinessPartnerFilterBy") {
    const rows = (cardValues as Partial<BusinessPartnersValues>).bpFilterRows;
    const index =
      rows?.findIndex(row => row.functionCode.trim() === second) ?? -1;
    return index >= 0 ? `bpFilterRows.${index}.functionCode` : "bpFilterRows";
  }
  if (head === "rolePricingAccessLevel") {
    const rows = (cardValues as Partial<PricingVisibilityValues>)
      .rolePricingRows;
    const index = rows?.findIndex(row => row.role.trim() === second) ?? -1;
    return index >= 0
      ? `rolePricingRows.${index}.accessLevel`
      : "rolePricingRows";
  }
  if (head === "pricingProcedureStepAccessLevels") {
    return /^\d+$/.test(second ?? "")
      ? `pricingStepRows.${second}.accessLevel`
      : "pricingStepRows";
  }
  if (head === "customFieldMetadataOverrides") {
    return "customFieldRows";
  }
  return SERVER_PATH_TO_FIELD[head] ?? head;
}

type SetFieldError = (field: string, message: string | undefined) => void;

const ROW_SX = {
  p: 1.5,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
} as const;

export const SapS4HanaSettings = ({
  integrationId,
  mode,
}: IntegrationSettingsProps) => {
  const { t } = useTranslate("Integrations");
  const meta = getIntegrationMeta(integrationId);
  const {
    orgIntegration,
    refetchDashboard,
    loading,
    removeLoading,
    handleAdd,
    handleUpdate,
    handleDisconnect,
    enqueueSnackbar,
  } = useIntegrationSettingsBase(integrationId, mode);

  const integrationName = meta.sidebarLabel ?? "SAP S/4HANA";
  const settings: SettingsObject =
    (orgIntegration?.settings as SettingsObject) ?? {};

  const isCreate = mode === "create";
  const [connecting, setConnecting] = React.useState(false);
  // Expert mode swaps the section cards for one whole-object JSON editor.
  // Session-local by design — a support tool, not a preference.
  const [expertMode, setExpertMode] = React.useState(false);
  // While a save/refetch is in flight every card's baseline is stale — a
  // second save would overlay its slice on the pre-save settings and restore
  // the first card's old values. Block all saves until the refetch lands.
  const [pageSaving, setPageSaving] = React.useState(false);

  const { anyDirty, dirtyOf } = useCardDirtyRegistry();
  useUnsavedChangesGuard(
    anyDirty,
    t("You have unsaved changes. Leave the page anyway?")
  );

  // Deep-link connect page: nothing is required to connect — the plugin
  // creates the integration with safe defaults. The normal path is the
  // connect modal on the overview; this page only backs stale links.
  if (isCreate) {
    return (
      <SettingsPageLayout
        integrationId={integrationId}
        integrationName={integrationName}
        title={`${t("Connect")} SAP S/4HANA`}
      >
        <CardContainer title={t("Connection")}>
          <Stack spacing={2} sx={{ px: 1, pt: 0.5, pb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t("All settings can be configured after connecting.")}
            </Typography>
            <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                disabled={connecting}
                onClick={async () => {
                  setConnecting(true);
                  try {
                    await handleAdd();
                  } catch (e) {
                    enqueueSnackbar(
                      t(
                        "Settings could not be saved. Please check your input."
                      ),
                      { variant: "error" }
                    );
                    setConnecting(false);
                  }
                }}
              >
                {t("Connect")}
              </Button>
            </Stack>
          </Stack>
        </CardContainer>
      </SettingsPageLayout>
    );
  }

  // Each card derives its own initial values from the stored settings and, on
  // save, contributes only the keys it owns — so a card can never overwrite a
  // setting it does not render (MEIS-12868). The CRM / GDPR card is a
  // deliberate exception for the `crm` object itself: it carries forward any
  // unrendered `crm` sub-keys (toCrmGdprSlice's existingSettings spread) but
  // still overwrites the sub-keys it does render — that scoping happens one
  // level below the top-level key this comment otherwise describes.
  const generalInitial = generalInitialValues(settings);
  const organisationInitial = organisationInitialValues(settings);
  const quotesInitial = quotesInitialValues(settings);
  const itemsInitial = itemsInitialValues(settings);
  const productsInitial = productsInitialValues(settings);
  const businessPartnersInitial = businessPartnersInitialValues(settings);
  const pricingVisibilityInitial = pricingVisibilityInitialValues(settings);
  const customFieldsInitial = customFieldsInitialValues(settings);
  const crmGdprInitial = crmGdprInitialValues(settings);
  const apiEndpointsInitial = apiEndpointsInitialValues(settings);
  const expertInitial = expertInitialValues(settings);

  const saveCard = async (
    slice: SettingsObject,
    cardValues: object,
    setFieldError: SetFieldError
  ) => {
    if (pageSaving) return;
    setPageSaving(true);
    try {
      await handleUpdate({ ...settings, ...slice });
      // handleUpdate does not refetch — the awaited refetch delivers the new
      // stored settings so enableReinitialize resets the saved card clean
      await refetchDashboard();
    } catch (e) {
      // Never surface the raw validation error (it echoes received
      // values) — highlight the offending fields instead. Only fields
      // belonging to the submitting card can be highlighted; anything
      // else falls back to the generic snackbar.
      const message = e instanceof Error ? e.message : String(e);
      const paths = serverValidationPaths(message);
      let highlighted = 0;
      for (const path of paths ?? []) {
        const field = serverPathToFormField(path, cardValues);
        const root = field.split(".")[0];
        if (Object.prototype.hasOwnProperty.call(cardValues, root)) {
          setFieldError(field, t("Invalid value"));
          highlighted++;
        }
      }
      enqueueSnackbar(
        highlighted > 0
          ? t("Please check the highlighted fields.")
          : t("Settings could not be saved. Please check your input."),
        { variant: "error" }
      );
    } finally {
      setPageSaving(false);
    }
  };

  const validateBusinessPartners = (values: BusinessPartnersValues) => {
    const duplicates = duplicateBpFunctionCodeIndexes(values.bpFilterRows);
    const rowErrors = values.bpFilterRows.map((row, index) => {
      if (duplicates.includes(index)) {
        return { functionCode: t("Duplicate partner function") };
      }
      if (row.functionCode.trim() === "") {
        return { functionCode: t("Partner function is required") };
      }
      return undefined;
    });
    return rowErrors.some(Boolean) ? { bpFilterRows: rowErrors } : {};
  };

  const accessLevelError = () =>
    t("Whole number, at least {min}", {
      ns: "Integrations",
      min: MIN_PRICING_ACCESS_LEVEL,
    });

  const validatePricingVisibility = (values: PricingVisibilityValues) => {
    const duplicateRoles = duplicateRoleIndexes(values.rolePricingRows);
    const roleErrors = values.rolePricingRows.map((row, index) => {
      const errors: { role?: string; accessLevel?: string } = {};
      if (duplicateRoles.includes(index)) errors.role = t("Duplicate role");
      else if (row.role.trim() === "") errors.role = t("Role is required");
      if (!isValidPricingAccessLevel(row.accessLevel)) {
        errors.accessLevel = accessLevelError();
      }
      return Object.keys(errors).length > 0 ? errors : undefined;
    });
    const duplicateSteps = duplicateStepIndexes(values.pricingStepRows);
    const stepErrors = values.pricingStepRows.map((row, index) => {
      const errors: { step?: string; accessLevel?: string } = {};
      if (duplicateSteps.includes(index)) {
        errors.step = t("Duplicate pricing procedure step");
      } else if (row.step.trim() === "") errors.step = t("Step is required");
      if (!isValidPricingAccessLevel(row.accessLevel)) {
        errors.accessLevel = accessLevelError();
      }
      return Object.keys(errors).length > 0 ? errors : undefined;
    });
    return {
      ...(roleErrors.some(Boolean) ? { rolePricingRows: roleErrors } : {}),
      ...(stepErrors.some(Boolean) ? { pricingStepRows: stepErrors } : {}),
    };
  };

  const validateCustomFields = (values: CustomFieldsValues) => {
    const duplicates = duplicateCustomFieldIndexes(values.customFieldRows);
    const rowErrors = values.customFieldRows.map((row, index) => {
      if (duplicates.includes(index)) {
        return { fieldKey: t("Duplicate field override") };
      }
      if (row.fieldKey.trim() === "") {
        return { fieldKey: t("Field key is required") };
      }
      return undefined;
    });
    return rowErrors.some(Boolean) ? { customFieldRows: rowErrors } : {};
  };

  const validateExpert = (values: ExpertValues) => {
    const parsed = parseExpertSettings(values.settingsJson);
    if (!parsed.ok) {
      return {
        settingsJson:
          parsed.error === "syntax"
            ? t("Invalid JSON")
            : t("Must be a JSON object"),
      };
    }
    const stepValue = parsed.value.pricingProcedureStepAccessLevels;
    if (stepValue !== undefined) {
      const code = pricingStepAccessLevelsError(stepValue);
      if (code === "duplicateStep") {
        return { settingsJson: t("Duplicate pricing procedure step") };
      }
      if (code !== null) {
        return {
          settingsJson: t(
            "Expected a list of steps with whole-number access levels of at least {min}",
            { ns: "Integrations", min: MIN_PRICING_ACCESS_LEVEL }
          ),
        };
      }
    }
    return {};
  };

  // Expert save deliberately replaces the whole settings object (no merge):
  // removing a key in the editor removes it from the stored settings. The
  // backend Zod schema remains the gate for structural validity.
  const saveExpert = async (
    values: ExpertValues,
    setFieldError: SetFieldError
  ) => {
    const parsed = parseExpertSettings(values.settingsJson);
    if (!parsed.ok) return;
    if (pageSaving) return;
    setPageSaving(true);
    try {
      await handleUpdate(parsed.value);
      await refetchDashboard();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      const highlighted = serverValidationPaths(message) !== null;
      if (highlighted) setFieldError("settingsJson", t("Invalid value"));
      enqueueSnackbar(
        highlighted
          ? t("Please check the highlighted fields.")
          : t("Settings could not be saved. Please check your input."),
        { variant: "error" }
      );
    } finally {
      setPageSaving(false);
    }
  };

  return (
    <SettingsPageLayout
      integrationId={integrationId}
      integrationName={integrationName}
      title={`${integrationName} — ${t("Settings")}`}
      onDisconnect={handleDisconnect}
      events={orgIntegration?.events ?? []}
      onRefreshEvents={refetchDashboard}
      hasDashboard={!!orgIntegration?.health}
      removeLoading={removeLoading}
      loading={loading && !orgIntegration}
    >
      {/* Flowing section cards instead of tabs (2026-07-14 review). Each
          card is an independent form with its own Save/Cancel row. */}
      <Stack spacing={2}>
        {/* Either/or editing model: expert mode swaps every card for one
            whole-object JSON editor, so no setting ever has two live
            editors. Toggling is blocked while anything is unsaved. */}
        <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={expertMode}
                disabled={anyDirty || pageSaving}
                onChange={event => setExpertMode(event.target.checked)}
              />
            }
            label={t("Expert mode (JSON)")}
          />
        </Stack>

        {expertMode ? (
          <Formik<ExpertValues>
            initialValues={expertInitial}
            validate={validateExpert}
            onSubmit={(values, { setFieldError }) =>
              saveExpert(values, setFieldError)
            }
            enableReinitialize
          >
            {fp => (
              <Form>
                <FormikClearErrorsOnChange />
                <FormDirtyTracker
                  dirty={fp.dirty}
                  onDirtyChange={dirtyOf("expert")}
                />
                <CardContainer title={t("Expert mode (JSON)")}>
                  <Stack spacing={2} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                    <Alert severity="info">
                      {t("Only change on support instructions.")}{" "}
                      {t(
                        "Saving replaces the entire settings object; a removed key is deleted."
                      )}
                    </Alert>
                    <TextField
                      name="settingsJson"
                      label={t("Settings (JSON)")}
                      size="small"
                      multiline
                      minRows={16}
                      maxRows={48}
                      slotProps={{
                        input: { sx: { fontFamily: "monospace" } },
                      }}
                    />
                    <CardSaveRow fp={fp} disabled={pageSaving} />
                  </Stack>
                </CardContainer>
              </Form>
            )}
          </Formik>
        ) : (
          <>
            <Formik<GeneralValues>
              initialValues={generalInitial}
              onSubmit={(values, { setFieldError }) =>
                saveCard(toGeneralSlice(values), values, setFieldError)
              }
              enableReinitialize
            >
              {fp => (
                <Form>
                  <FormikClearErrorsOnChange />
                  <FormDirtyTracker
                    dirty={fp.dirty}
                    onDirtyChange={dirtyOf("general")}
                  />
                  <CardContainer title={t("General")}>
                    <Stack spacing={1} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                      <TextField
                        name="sapPartnerId"
                        label="sapPartnerId"
                        size="small"
                      />
                      <TextField
                        name="salesQuotationType"
                        label="salesQuotationType"
                        size="small"
                      />
                      <TextField
                        name="importFromSalesQuotationType"
                        label="importFromSalesQuotationType"
                        size="small"
                      />
                      <TextField
                        name="defaultProductSupplierId"
                        label="defaultProductSupplierId"
                        size="small"
                      />
                      <TextField
                        name="salesQuotationCreatedByUser"
                        label="salesQuotationCreatedByUser"
                        size="small"
                      />
                      <TextField
                        name="salesQuotationQuoteIdCustomFieldKey"
                        label="salesQuotationQuoteIdCustomFieldKey"
                        size="small"
                      />

                      <Typography variant="subtitle2" sx={{ pt: 1 }}>
                        {t("UI Toggles")}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={fp.values.hideResetPassword}
                            onChange={e =>
                              fp.setFieldValue(
                                "hideResetPassword",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t("Hide reset password button")}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={fp.values.hideDeleteProfile}
                            onChange={e =>
                              fp.setFieldValue(
                                "hideDeleteProfile",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t("Hide delete profile button")}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={fp.values.disableCraftsmenAccountingFields}
                            onChange={e =>
                              fp.setFieldValue(
                                "disableCraftsmenAccountingFields",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t("Disable craftsmen accounting fields")}
                      />
                      <CardSaveRow fp={fp} disabled={pageSaving} />
                    </Stack>
                  </CardContainer>
                </Form>
              )}
            </Formik>

            <Formik<OrganisationValues>
              initialValues={organisationInitial}
              onSubmit={(values, { setFieldError }) =>
                saveCard(toOrganisationSlice(values), values, setFieldError)
              }
              enableReinitialize
            >
              {fp => (
                <Form>
                  <FormikClearErrorsOnChange />
                  <FormDirtyTracker
                    dirty={fp.dirty}
                    onDirtyChange={dirtyOf("organisation")}
                  />
                  <CardContainer title={t("Organisation")}>
                    <Stack spacing={1} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                      <TextField
                        name="availableSalesOrganizations"
                        label={t(
                          "Available Sales Organizations (comma-separated)"
                        )}
                        size="small"
                      />
                      <TextField
                        name="distributionChannel"
                        label="distributionChannel"
                        size="small"
                      />
                      <TextField
                        name="organizationDivision"
                        label="organizationDivision"
                        size="small"
                      />
                      <TextField
                        name="fioriBusinessPartnerBasePath"
                        label="fioriBusinessPartnerBasePath"
                        size="small"
                      />
                      <TextField
                        name="clientCertificateSecretId"
                        label="clientCertificateSecretId"
                        size="small"
                      />
                      <CardSaveRow fp={fp} disabled={pageSaving} />
                    </Stack>
                  </CardContainer>
                </Form>
              )}
            </Formik>

            <Formik<QuotesValues>
              initialValues={quotesInitial}
              onSubmit={(values, { setFieldError }) =>
                saveCard(toQuotesSlice(values), values, setFieldError)
              }
              enableReinitialize
            >
              {fp => (
                <Form>
                  <FormikClearErrorsOnChange />
                  <FormDirtyTracker
                    dirty={fp.dirty}
                    onDirtyChange={dirtyOf("quotes")}
                  />
                  <CardContainer title={t("Quotes")}>
                    <Stack spacing={1} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                      <TextField
                        name="salesQuotationStatuses"
                        label={t("Quotation Statuses (comma-separated)")}
                        size="small"
                      />
                      <TextField
                        name="salesQuotationInitialStatus"
                        label="salesQuotationInitialStatus"
                        size="small"
                      />
                      <TextField
                        name="salesQuotationStatusField"
                        label="salesQuotationStatusField"
                        size="small"
                      />
                      <TextField
                        name="salesQuotationSdDocumentReasons"
                        label={t("SD Document Reasons (comma-separated)")}
                        size="small"
                      />
                      <TextField
                        name="salesQuotationItemSalesDocumentRejectionReasonDefault"
                        label={t("Default Rejection Reason")}
                        size="small"
                      />
                      <TextField
                        name="salesQuotationItemSalesDocumentRejectionReasons"
                        label={t("Item Rejection Reasons (comma-separated)")}
                        size="small"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={
                              fp.values
                                .salesQuotationHandleDeletedItemsAsRejected
                            }
                            onChange={e =>
                              fp.setFieldValue(
                                "salesQuotationHandleDeletedItemsAsRejected",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t("Handle deleted items as rejected")}
                      />
                      <TextField
                        name="salesQuotationDummyMaterialNumbers"
                        label={t("Dummy Material Numbers (comma-separated)")}
                        size="small"
                      />
                      <CardSaveRow fp={fp} disabled={pageSaving} />
                    </Stack>
                  </CardContainer>
                </Form>
              )}
            </Formik>

            <Formik<ItemsValues>
              initialValues={itemsInitial}
              onSubmit={(values, { setFieldError }) =>
                saveCard(toItemsSlice(values), values, setFieldError)
              }
              enableReinitialize
            >
              {fp => (
                <Form>
                  <FormikClearErrorsOnChange />
                  <FormDirtyTracker
                    dirty={fp.dirty}
                    onDirtyChange={dirtyOf("items")}
                  />
                  <CardContainer title={t("Items")}>
                    <Stack spacing={1} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                      <Typography variant="subtitle2">
                        {t("Position settings")}
                      </Typography>
                      <TextField
                        name="positionStepSize"
                        label={t("Position Step Size")}
                        type="number"
                        size="small"
                      />
                      <TextField
                        name="roundDistributedPositionsTo"
                        label={t("Round Distributed Positions To")}
                        type="number"
                        size="small"
                      />
                      <TextField
                        name="firstPosition"
                        label={t("First Position")}
                        type="number"
                        size="small"
                      />
                      <TextField
                        name="minPosition"
                        label={t("Min Position")}
                        type="number"
                        size="small"
                      />
                      <TextField
                        name="maxPosition"
                        label={t("Max Position (empty = no limit)")}
                        type="number"
                        size="small"
                      />

                      <Typography variant="subtitle2" sx={{ pt: 1 }}>
                        {t("Item categories")}
                      </Typography>
                      <TextField
                        name="salesQuotationItemSectionItemCategory"
                        label="salesQuotationItemSectionItemCategory"
                        size="small"
                      />
                      <TextField
                        name="salesQuotationItemComponentCategory"
                        label="salesQuotationItemComponentCategory"
                        size="small"
                      />
                      <TextField
                        name="salesQuotationItemHeaderCategories"
                        label={t("Header Categories (comma-separated)")}
                        size="small"
                      />
                      <TextField
                        name="salesQuotationItemSectionItemBeginItemMaterial"
                        label="salesQuotationItemSectionItemBeginItemMaterial"
                        size="small"
                      />
                      <TextField
                        name="salesQuotationItemSectionItemEndItemMaterial"
                        label="salesQuotationItemSectionItemEndItemMaterial"
                        size="small"
                      />
                      <TextField
                        name="salesQuotationItemPlantDefault"
                        label="salesQuotationItemPlantDefault"
                        size="small"
                      />
                      <TextField
                        name="itemCategoryOverrides"
                        label={t("Item Category Overrides (comma-separated)")}
                        size="small"
                      />
                      <CardSaveRow fp={fp} disabled={pageSaving} />
                    </Stack>
                  </CardContainer>
                </Form>
              )}
            </Formik>

            <Formik<ProductsValues>
              initialValues={productsInitial}
              onSubmit={(values, { setFieldError }) =>
                saveCard(toProductsSlice(values), values, setFieldError)
              }
              enableReinitialize
            >
              {fp => (
                <Form>
                  <FormikClearErrorsOnChange />
                  <FormDirtyTracker
                    dirty={fp.dirty}
                    onDirtyChange={dirtyOf("products")}
                  />
                  <CardContainer title={t("Products")}>
                    <Stack spacing={1} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                      <TextField
                        name="configurableProductSupplierCatalogueIds"
                        label={t(
                          "Configurable Product Catalogue IDs (comma-separated)"
                        )}
                        size="small"
                      />
                      <TextField
                        name="kmatListingAssortment"
                        label="kmatListingAssortment"
                        size="small"
                      />
                      <TextField
                        name="fisSolrUrl"
                        label={t("FIS SOLR URL (optional)")}
                        size="small"
                      />
                      <CardSaveRow fp={fp} disabled={pageSaving} />
                    </Stack>
                  </CardContainer>
                </Form>
              )}
            </Formik>

            <Formik<BusinessPartnersValues>
              initialValues={businessPartnersInitial}
              validate={validateBusinessPartners}
              onSubmit={(values, { setFieldError }) =>
                saveCard(toBusinessPartnersSlice(values), values, setFieldError)
              }
              enableReinitialize
            >
              {fp => (
                <Form>
                  <FormikClearErrorsOnChange />
                  <FormDirtyTracker
                    dirty={fp.dirty}
                    onDirtyChange={dirtyOf("businessPartners")}
                  />
                  <CardContainer title={t("Business Partners")}>
                    <Stack spacing={2} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          "Filters applied per partner function when picking business partners for a quote."
                        )}
                      </Typography>
                      <FieldArray name="bpFilterRows">
                        {({ push, remove }) => (
                          <>
                            {fp.values.bpFilterRows.map((_, index) => (
                              <Stack key={index} spacing={1} sx={ROW_SX}>
                                <Stack
                                  direction="row"
                                  sx={{
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    {t("Partner function")} {index + 1}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => remove(index)}
                                    aria-label={t("Remove partner function")}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                                <TextField
                                  name={`bpFilterRows.${index}.functionCode`}
                                  label={t("Partner function code")}
                                  size="small"
                                />
                                <TextField
                                  name={`bpFilterRows.${index}.businessPartnerRolesAll`}
                                  label={t("Required Roles (comma-separated)")}
                                  size="small"
                                />
                                <TextField
                                  name={`bpFilterRows.${index}.businessPartnerRolesExclude`}
                                  label={t("Excluded Roles (comma-separated)")}
                                  size="small"
                                />
                                <TextField
                                  name={`bpFilterRows.${index}.tagsExclude`}
                                  label={t("Excluded Tags (comma-separated)")}
                                  size="small"
                                />
                              </Stack>
                            ))}
                            <Stack direction="row">
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                  push({
                                    functionCode: "",
                                    businessPartnerRolesAll: "",
                                    businessPartnerRolesExclude: "",
                                    tagsExclude: "",
                                  })
                                }
                              >
                                {t("Add partner function")}
                              </Button>
                            </Stack>
                          </>
                        )}
                      </FieldArray>
                      <TextField
                        name="preselectedQuoteBusinessPartnerFunctions"
                        label={t(
                          "Preselected Quote Partner Functions (comma-separated)"
                        )}
                        size="small"
                      />
                      <CardSaveRow fp={fp} disabled={pageSaving} />
                    </Stack>
                  </CardContainer>
                </Form>
              )}
            </Formik>

            <Formik<PricingVisibilityValues>
              initialValues={pricingVisibilityInitial}
              validate={validatePricingVisibility}
              onSubmit={(values, { setFieldError }) =>
                saveCard(
                  toPricingVisibilitySlice(values),
                  values,
                  setFieldError
                )
              }
              enableReinitialize
            >
              {fp => (
                <Form>
                  <FormikClearErrorsOnChange />
                  <FormDirtyTracker
                    dirty={fp.dirty}
                    onDirtyChange={dirtyOf("pricingVisibility")}
                  />
                  <CardContainer title={t("Pricing visibility")}>
                    <Stack spacing={2} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          "Highest pricing access level per role. 1000 is the least restrictive level (all rows); higher values are more restricted. A role without an entry falls back to 1000."
                        )}
                      </Typography>
                      <FieldArray name="rolePricingRows">
                        {({ push, remove }) => (
                          <>
                            {fp.values.rolePricingRows.map((_, index) => (
                              <Stack
                                key={index}
                                direction="row"
                                spacing={1}
                                sx={{ alignItems: "flex-start" }}
                              >
                                <TextField
                                  name={`rolePricingRows.${index}.role`}
                                  label={t("Role")}
                                  size="small"
                                />
                                <TextField
                                  name={`rolePricingRows.${index}.accessLevel`}
                                  label={t("Access level")}
                                  type="number"
                                  size="small"
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => remove(index)}
                                  aria-label={t("Remove role")}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            ))}
                            <Stack direction="row">
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                  push({
                                    role: "",
                                    accessLevel: MIN_PRICING_ACCESS_LEVEL,
                                  })
                                }
                              >
                                {t("Add role")}
                              </Button>
                            </Stack>
                          </>
                        )}
                      </FieldArray>

                      <Typography variant="subtitle2" sx={{ pt: 1 }}>
                        {t("Pricing procedure steps")}
                      </Typography>
                      <FieldArray name="pricingStepRows">
                        {({ push, remove }) => (
                          <>
                            {fp.values.pricingStepRows.map((_, index) => (
                              <Stack
                                key={index}
                                direction="row"
                                spacing={1}
                                sx={{ alignItems: "flex-start" }}
                              >
                                <TextField
                                  name={`pricingStepRows.${index}.step`}
                                  label={t("Step")}
                                  size="small"
                                />
                                <TextField
                                  name={`pricingStepRows.${index}.accessLevel`}
                                  label={t("Access level")}
                                  type="number"
                                  size="small"
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => remove(index)}
                                  aria-label={t("Remove step")}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            ))}
                            <Stack direction="row">
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                  push({
                                    step: "",
                                    accessLevel: MIN_PRICING_ACCESS_LEVEL,
                                  })
                                }
                              >
                                {t("Add step")}
                              </Button>
                            </Stack>
                          </>
                        )}
                      </FieldArray>
                      <CardSaveRow fp={fp} disabled={pageSaving} />
                    </Stack>
                  </CardContainer>
                </Form>
              )}
            </Formik>

            <Formik<CustomFieldsValues>
              initialValues={customFieldsInitial}
              validate={validateCustomFields}
              onSubmit={(values, { setFieldError }) =>
                saveCard(toCustomFieldsSlice(values), values, setFieldError)
              }
              enableReinitialize
            >
              {fp => (
                <Form>
                  <FormikClearErrorsOnChange />
                  <FormDirtyTracker
                    dirty={fp.dirty}
                    onDirtyChange={dirtyOf("customFields")}
                  />
                  <CardContainer title={t("Custom Fields")}>
                    <Stack spacing={2} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          "Per-organisation overrides for managed custom fields, e.g. disabling a field."
                        )}
                      </Typography>
                      <FieldArray name="customFieldRows">
                        {({ push, remove }) => (
                          <>
                            {fp.values.customFieldRows.map((_, index) => (
                              <Stack
                                key={index}
                                direction="row"
                                spacing={1}
                                sx={{ alignItems: "flex-start" }}
                              >
                                <SelectField<string>
                                  name={`customFieldRows.${index}.entity`}
                                  label={t("Entity")}
                                  size="small"
                                  options={CUSTOM_FIELD_ENTITIES.map(
                                    entity => ({
                                      value: entity,
                                      label: entity,
                                    })
                                  )}
                                />
                                <TextField
                                  name={`customFieldRows.${index}.fieldKey`}
                                  label={t("Field key")}
                                  size="small"
                                />
                                <FormControlLabel
                                  sx={{ whiteSpace: "nowrap", pt: 0.5 }}
                                  control={
                                    <Switch
                                      size="small"
                                      checked={
                                        fp.values.customFieldRows[index]
                                          .disabled
                                      }
                                      onChange={event =>
                                        fp.setFieldValue(
                                          `customFieldRows.${index}.disabled`,
                                          event.target.checked
                                        )
                                      }
                                    />
                                  }
                                  label={t("Disabled")}
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => remove(index)}
                                  aria-label={t("Remove field override")}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            ))}
                            <Stack direction="row">
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                  push({
                                    entity: CUSTOM_FIELD_ENTITIES[0],
                                    fieldKey: "",
                                    disabled: false,
                                  })
                                }
                              >
                                {t("Add field override")}
                              </Button>
                            </Stack>
                          </>
                        )}
                      </FieldArray>
                      <CardSaveRow fp={fp} disabled={pageSaving} />
                    </Stack>
                  </CardContainer>
                </Form>
              )}
            </Formik>

            <Formik<CrmGdprValues>
              initialValues={crmGdprInitial}
              onSubmit={(values, { setFieldError }) =>
                saveCard(
                  toCrmGdprSlice(values, settings),
                  values,
                  setFieldError
                )
              }
              enableReinitialize
            >
              {fp => (
                <Form>
                  <FormikClearErrorsOnChange />
                  <FormDirtyTracker
                    dirty={fp.dirty}
                    onDirtyChange={dirtyOf("crmGdpr")}
                  />
                  <CardContainer title={t("CRM / GDPR")}>
                    <Stack spacing={1} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                      <Typography variant="subtitle2">{t("CRM")}</Typography>
                      <TextField
                        name="crmUserIdCustomFieldKey"
                        label={t("User ID Custom Field Key")}
                        size="small"
                      />
                      <TextField
                        name="crmDefaultJobTitle"
                        label={t("Default Job Title")}
                        size="small"
                      />
                      <TextField
                        name="crmDefaultCashPlanningGroup"
                        label={t("Default Cash Planning Group")}
                        size="small"
                      />
                      <TextField
                        name="crmEndClientPaymentTerms"
                        label={t("End client payment terms")}
                        size="small"
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={fp.values.crmEndClientZugferdMailEnabled}
                            onChange={e =>
                              fp.setFieldValue(
                                "crmEndClientZugferdMailEnabled",
                                e.target.checked
                              )
                            }
                          />
                        }
                        label={t("Send ZUGFeRD e-mail on end client creation")}
                      />

                      <Typography variant="subtitle2" sx={{ pt: 1 }}>
                        {t("GDPR")}
                      </Typography>
                      <TextField
                        name="gdprFromEmailAddress"
                        label={t("From Email Address")}
                        size="small"
                      />
                      <TextField
                        name="gdprEmailTemplateId"
                        label={t("Email Template ID")}
                        size="small"
                      />
                      <CardSaveRow fp={fp} disabled={pageSaving} />
                    </Stack>
                  </CardContainer>
                </Form>
              )}
            </Formik>

            <Formik<ApiEndpointsValues>
              initialValues={apiEndpointsInitial}
              onSubmit={(values, { setFieldError }) =>
                saveCard(toApiEndpointsSlice(values), values, setFieldError)
              }
              enableReinitialize
            >
              {fp => (
                <Form>
                  <FormikClearErrorsOnChange />
                  <FormDirtyTracker
                    dirty={fp.dirty}
                    onDirtyChange={dirtyOf("apiEndpoints")}
                  />
                  <CardContainer
                    title={t("API Endpoints")}
                    isExpandable
                    isInitiallyClosed
                  >
                    <Stack spacing={2} sx={{ px: 1, pt: 0.5, pb: 1 }}>
                      <Alert severity="info">
                        {t("Only change on support instructions.")}
                      </Alert>
                      {API_ENDPOINT_GROUPS.map(group => (
                        <Stack key={group.title} spacing={1}>
                          <Typography variant="subtitle2">
                            {group.title}
                          </Typography>
                          {group.endpoints.map(pair => (
                            <Stack key={pair[0]} spacing={0.5}>
                              <TextField
                                name={pair[0]}
                                label={pair[0]}
                                size="small"
                                slotProps={{
                                  input: { sx: { fontFamily: "monospace" } },
                                }}
                              />
                              {pair[1] && (
                                <TextField
                                  name={pair[1]}
                                  label={pair[1]}
                                  size="small"
                                  slotProps={{
                                    input: { sx: { fontFamily: "monospace" } },
                                  }}
                                />
                              )}
                            </Stack>
                          ))}
                        </Stack>
                      ))}
                      <CardSaveRow fp={fp} disabled={pageSaving} />
                    </Stack>
                  </CardContainer>
                </Form>
              )}
            </Formik>
          </>
        )}
      </Stack>
    </SettingsPageLayout>
  );
};
